import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { database } from "../../firebase";
import { ref, onValue, update, off, get } from "firebase/database";
import "./PhotoCapture.css";
import { idbPut, idbDelete } from "./indexeddb";
import { resizeAndCompressImage } from "./image-compress";
import { MAX_WIDTH, MAX_HEIGHT, JPEG_QUALITY } from "./config.state";
import { uploadToSignedUrl } from "./upload-to-signed-url";

const STRICT_SAME_POINT_DISTANCE = 15; // meters

/* ---------- DISTANCE LOGIC (FROM FUNCTION) ---------- */

function distanceMeters(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) *
    Math.cos(φ2) *
    Math.sin(Δλ / 2) *
    Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export default function PhotoCapture({ userID, uniqueKey, onDone, onPhotoDone }) {
  const recordPath = `data/scans/${userID}/${uniqueKey}`;
  const parentRef = ref(database, recordPath);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const signedUrlListenerRef = useRef(null);
  const lastCompressedRef = useRef(null);
  const lastFilenameRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [scanAddress, setScanAddress] = useState("");
  const [currentDistance, setCurrentDistance] = useState(null);

  useEffect(() => {
    // Fetch scan address from database
    const fetchAddress = async () => {
      try {
        const snapshot = await get(parentRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const addr = data.editedAddress || data.googleMapAddress || "Unknown location";
          setScanAddress(addr);
        }
      } catch (err) {
        console.error("Failed to fetch address:", err);
      }
    };

    fetchAddress();
    setLoading(false);
    return () => {
      cleanupSignedUrlListener();
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, uniqueKey]);

  const isMobile = () => /Mobi|Android/i.test(navigator.userAgent || "");

  /* ---------------- LOCATION + VALIDATION ---------------- */

  const getCurrentLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            x: pos.coords.latitude,
            y: pos.coords.longitude,
          });
        },
        () => reject(new Error("Location permission denied")),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const validateCorrectLocation = async () => {
    const coordsSnap = await get(
      ref(database, `${recordPath}/coordinates`)
    );
    const origin = coordsSnap.val();

    if (
      !origin ||
      !isFinite(origin.x) ||
      !isFinite(origin.y)
    ) {
      throw new Error("Missing or invalid original scan coordinates");
    }

    const current = await getCurrentLocation();

    if (
      !isFinite(current.x) ||
      !isFinite(current.y)
    ) {
      throw new Error("Invalid current location");
    }

    const dist = distanceMeters(
      origin.x,
      origin.y,
      current.x,
      current.y
    );

    console.log(`Measured Distance: ${dist} meters`);
    setCurrentDistance(dist);

    if (dist > STRICT_SAME_POINT_DISTANCE) {
      const error = new Error(
        `You are ${Math.round(dist)} meters away from the scan location. Please move closer (within ${STRICT_SAME_POINT_DISTANCE} meters) and try again.`
      );
      error.distance = dist;
      throw error;
    }
  };

  /* ---------------- CAMERA FLOW ---------------- */

  const triggerFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const openPicker = async () => {
    try {
      await validateCorrectLocation();
      setCurrentDistance(null); // Clear distance on success
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message);
      return;
    }

    if (isMobile()) {
      triggerFileInput();
      return;
    }

    if (navigator.mediaDevices?.getUserMedia) {
      try {
        await openCameraInPage();
      } catch {
        triggerFileInput();
      }
      return;
    }

    triggerFileInput();
  };

  async function openCameraInPage() {
    if (cameraOpen) return;

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: "environment",
      },
    });

    streamRef.current = stream;
    videoRef.current.srcObject = stream;
    await videoRef.current.play();
    setCameraOpen(true);
  }

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  }

  /* ---------------- CAPTURE + UPLOAD (UNCHANGED) ---------------- */

  const captureFromCamera = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Get original video dimensions
    const videoWidth = video.videoWidth || 1280;
    const videoHeight = video.videoHeight || 720;

    // Limit canvas size to prevent memory crashes on high-res cameras
    const MAX_CAPTURE_SIZE = 1280;
    let captureWidth = videoWidth;
    let captureHeight = videoHeight;

    if (videoWidth > MAX_CAPTURE_SIZE || videoHeight > MAX_CAPTURE_SIZE) {
      const aspect = videoWidth / videoHeight;
      if (aspect > 1) {
        // Landscape
        captureWidth = MAX_CAPTURE_SIZE;
        captureHeight = Math.round(MAX_CAPTURE_SIZE / aspect);
      } else {
        // Portrait
        captureHeight = MAX_CAPTURE_SIZE;
        captureWidth = Math.round(MAX_CAPTURE_SIZE * aspect);
      }
    }

    canvas.width = captureWidth;
    canvas.height = captureHeight;

    canvas.getContext("2d").drawImage(video, 0, 0, captureWidth, captureHeight);

    canvas.toBlob(async (blob) => {
      if (!blob) return;

      stopCamera();
      await processFile(
        new File(
          [blob],
          `${userID}_${uniqueKey}_${Date.now()}.jpg`,
          { type: "image/jpeg" }
        )
      );
    }, "image/jpeg", 0.8);
  };

  const processFile = async (file) => {
    setStatus("saving");
    setErrorMsg(null);

    try {
      const compressed = await resizeAndCompressImage(
        file,
        MAX_WIDTH,
        MAX_HEIGHT,
        JPEG_QUALITY
      );

      const filename = `${userID}_${uniqueKey}_${Date.now()}.jpg`;
      await idbPut(filename, compressed);

      lastCompressedRef.current = compressed;
      lastFilenameRef.current = filename;

      // Set status immediately for optimistic UI update
      await update(parentRef, { requestToUpload: 1 });
      setStatus("waiting-for-signed-url");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message);
      return;
    }

    cleanupSignedUrlListener();

    signedUrlListenerRef.current = attachSignedUrlListener(async (url) => {
      if (!lastCompressedRef.current) return;

      setStatus("uploading");
      await uploadToSignedUrl(url, lastCompressedRef.current);
      await update(parentRef, { updateTimestamp: Date.now() });

      setStatus("uploaded");
      await idbDelete(lastFilenameRef.current).catch(() => { });
      setTimeout(callDoneCallback, 300);
    });
  };

  /* ---------------- HELPERS ---------------- */

  function attachSignedUrlListener(cb) {
    const signedRef = ref(database, `${recordPath}/signedUrlToUploadImage`);
    const unsub = onValue(signedRef, (snap) => snap.val() && cb(snap.val()));
    return () => off(signedRef) || unsub?.();
  }

  function cleanupSignedUrlListener() {
    signedUrlListenerRef.current?.();
    signedUrlListenerRef.current = null;
  }

  const callDoneCallback = () =>
    onPhotoDone?.() || onDone?.();

  const handleTryAgain = () => {
    setStatus(null);
    setErrorMsg(null);
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="pc-card">
      <h3 className="pc-title">Capture Photo</h3>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => processFile(e.target.files?.[0])}
        style={{ display: "none" }}
      />

      {cameraOpen && (
        <div className="pc-camera-preview">
          <video ref={videoRef} className="pc-video" autoPlay playsInline />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <button onClick={captureFromCamera}>Take Photo</button>
          <button onClick={stopCamera}>Cancel</button>
        </div>
      )}


      {!cameraOpen && !status && (
        <>
          <button onClick={openPicker}>Open Camera</button>
          <button
            onClick={callDoneCallback}
            style={{
              marginTop: "10px",
              marginBottom: "20px",
              backgroundColor: "#757575",
              borderColor: "#757575"
            }}
          >
            Back to Dashboard
          </button>
        </>
      )}

      {status && status !== "error" && status !== "uploading" && status !== "uploaded" && (
        <>
          <div className="pc-status-message" style={{ textAlign: "center", marginTop: "10px", fontWeight: "bold" }}>
            Status: {status}
          </div>
          <button
            onClick={callDoneCallback}
            style={{
              marginTop: "10px",
              marginBottom: "20px",
              backgroundColor: "#757575",
              borderColor: "#757575"
            }}
          >
            Back to Dashboard
          </button>
        </>
      )}

      {(status === "uploading" || status === "uploaded") && (
        <div className="pc-status-message" style={{ textAlign: "center", marginTop: "10px", fontWeight: "bold" }}>
          Status: {status}
        </div>
      )}


      {status === "error" && (
        <div className="pc-error">
          <div style={{ marginBottom: "15px" }}>
            <strong>Location:</strong>
            <div style={{ marginTop: "5px", color: "#666" }}>{scanAddress}</div>
          </div>

          {currentDistance !== null && (
            <div style={{ marginBottom: "15px", fontSize: "0.9rem" }}>
              <strong>Current Distance:</strong> {Math.round(currentDistance)} meters
              <br />
              <strong>Required:</strong> Within {STRICT_SAME_POINT_DISTANCE} meters
            </div>
          )}

          <div style={{ color: "#d32f2f", marginBottom: "15px" }}>
            {errorMsg}
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <button onClick={handleTryAgain}>Try Again</button>
            <button
              onClick={() => {
                callDoneCallback();
              }}
              style={{
                backgroundColor: "#757575",
                borderColor: "#757575"
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

PhotoCapture.propTypes = {
  userID: PropTypes.string.isRequired,
  uniqueKey: PropTypes.string.isRequired,
  onDone: PropTypes.func,
  onPhotoDone: PropTypes.func,
};
